import useAuth from "./hooks/useAuth";
import { useEffect, useState } from "react";

const UserWords = ({language, date}) => {
    const {auth, setAuth} = useAuth();
    const [stats, setStats] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
   
    useEffect(() => { 
        setCurrentPage(1); // Reset to first page when filters change
        getMyStats();
    }, [language, date]);

    const snap = new Audio();
  
    const Play = (audio) => {
        if (audio) {
            snap.src = `${process.env.REACT_APP_USER_API_URL}/` + audio;
            snap.load();
            snap.play();
        }       
    };

    const getMyStats = async () => {
        const token = localStorage.getItem("token");
        setLoading(true);
        
        try {
            let url = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/words`;
            
            if (date !== 'Select date' && date !== 'All records') {
                url += `?date=${date}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const json = await response.json();
                setStats(json || []);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate pagination values
    const totalItems = stats.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentStats = stats.slice(startIndex, endIndex);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        // Scroll to top when changing pages
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page
    };

    return (
        <div className="w-full">
            {/* Pagination Controls - Top */}
            {stats.length > 0 && (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600">
                        Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} words
                    </div>
                    
                    <div className="flex items-center gap-4">
                        {/* Items per page selector */}
                        <select 
                            value={itemsPerPage}
                            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-3 py-1 bg-white"
                        >
                            <option value={5}>5 per page</option>
                            <option value={10}>10 per page</option>
                            <option value={20}>20 per page</option>
                            <option value={50}>50 per page</option>
                        </select>

                        {/* Page navigation */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                            >
                                ←
                            </button>
                            
                            <span className="text-sm text-gray-700 min-w-[80px] text-center">
                                Page {currentPage} of {totalPages}
                            </span>
                            
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                            >
                                →
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Loading your words...</p>
                </div>
            )}

            {/* Content */}
            {!loading && currentStats.length > 0 ? (
                <div className="grid grid-flow-row-dense grid-cols-2 mx-auto gap-y-5 gap-x-5 md:p-10">
                    <p className="text-sm md:text-md text-blue-600 text-center">Word</p>
                    <p className="text-sm md:text-md text-blue-600 text-center">Sentence</p>
                   
                    {currentStats.map((word, i) => {
                        const accuracyScore = word["word"]?.['PronunciationAssessment']?.['AccuracyScore'] || 0;
                        
                        return (
                            <div key={i} className="contents">
                                {/* Word Column */}
                                <div className={`flex flex-col gap-2 border-2 rounded-xl p-4 ${
                                    accuracyScore >= 80 ? "bg-green-200" : 
                                    accuracyScore >= 40 ? "bg-yellow-200" : "bg-red-200"
                                }`}>
                                    <p className='text-md md:text-3xl text-center font-medium'>
                                        {word["word"]?.["Word"] || "N/A"}
                                    </p> 
   
                                    {/* Phonemes visualization */}
                                    {(word["word"]?.hasOwnProperty("Phonemes")) && (
                                        <div className='mx-auto flex flex-row gap-1 flex-wrap justify-center'>
                                            {word["word"]["Phonemes"].map((ph, phIndex) => (
                                                <div 
                                                    key={phIndex}
                                                    className={`h-4 w-4 border-2 rounded-xl ${
                                                        ph.PronunciationAssessment?.AccuracyScore >= 80 ? "bg-green-500" : 
                                                        ph.PronunciationAssessment?.AccuracyScore >= 40 ? "bg-yellow-700" : "bg-red-500"
                                                    }`}
                                                    title={`${ph.Phoneme || ''} (${ph.PronunciationAssessment?.AccuracyScore || 0})`}
                                                ></div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Additional phonemes display */}
                                    {(word.hasOwnProperty("Phonemes")) && (
                                        <div className='mx-auto flex flex-row gap-2 flex-wrap justify-center text-sm'>
                                            {word["Phonemes"].map((ph, phIndex) => (
                                                <div key={phIndex}>{ph}</div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    <p className='text-sm md:text-xl text-center font-bold text-blue-600'>
                                        Score: {accuracyScore}
                                    </p> 
                       
                                    {/* Detailed phoneme scores */}
                                    {(word["word"]?.hasOwnProperty("Phonemes")) && (
                                        <div className="mx-auto flex flex-row flex-wrap gap-1 justify-center text-xs">
                                            {word["word"]["Phonemes"].map((ph, phIndex) => (
                                                <div key={phIndex}>
                                                    {(ph["Phoneme"] !== "") && `${ph["Phoneme"]}(${ph["PronunciationAssessment"]?.["AccuracyScore"] || 0})`}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                       
                                {/* Sentence Column */}
                                <button 
                                    className="border-2 rounded-xl hover:bg-blue-100 bg-blue-50 p-4 transition-colors"
                                    onClick={() => Play(word['audio'])}
                                >
                                    <div>
                                        <p className="hover:text-blue-600 text-blue-400 text-sm md:text-lg break-words">
                                            {word["text"] || "No sentence available"}
                                        </p>
                                    </div>
                                </button>
                            </div>
                        );
                    })}
                </div>
            ) : !loading && date !== 'Select date' ? (
                <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-2">📝</div>
                    <p className="text-gray-500">No words found for the selected criteria</p>
                </div>
            ) : !loading && (
                <div className="text-center py-8">
                    <p className="text-gray-500">Select a date to view your words</p>
                </div>
            )}

            {/* Pagination Controls - Bottom */}
            {stats.length > 0 && totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-6 pt-4 border-t border-gray-200">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-sm"
                    >
                        First
                    </button>
                    
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                        ← Previous
                    </button>
                    
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                        Next →
                    </button>
                    
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-sm"
                    >
                        Last
                    </button>
                </div>
            )}
        </div>
    );
}

export default UserWords;